import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerCorrectiveActionsApi } from '../../api/partner-governance-references-actions';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

/** Corrective Actions (spec section 33) - shows everything for this partner, whether created automatically from an "Approved with Conditions" decision or added manually here. */
export function PartnerCorrectiveActionsTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const actionsQuery = useQuery({ queryKey: ['partner-corrective-actions', partnerId], queryFn: () => partnerCorrectiveActionsApi.listForPartner(partnerId) });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => partnerCorrectiveActionsApi.setStatus(id, status),
    onSuccess: () => { showSuccess('Status updated.'); queryClient.invalidateQueries({ queryKey: ['partner-corrective-actions', partnerId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {actionsQuery.data && (
          <DataTable
            rows={actionsQuery.data.data}
            rowKey={(a) => a.id}
            emptyMessage="No corrective actions on file. These appear automatically when a case is Approved with Conditions, or can be added manually during a review."
            columns={[
              { header: 'Finding', render: (a) => a.finding },
              { header: 'Action Required', render: (a) => a.action_required },
              { header: 'Responsible', render: (a) => a.responsible_person ?? '-' },
              { header: 'Due Date', render: (a) => a.due_date ?? '-' },
              { header: 'Priority', render: (a) => a.priority },
              {
                header: 'Status', render: (a) => (
                  <select value={a.status} onChange={(e) => statusMutation.mutate({ id: a.id, status: e.target.value })}>
                    <option value="open">Open</option><option value="in_progress">In Progress</option><option value="submitted">Submitted</option>
                    <option value="verified">Verified</option><option value="overdue">Overdue</option><option value="closed">Closed</option>
                  </select>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
